import React, { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ProjectBoard from './pages/ProjectBoard';
import ProjectList from './pages/ProjectList';
import CreateProject from './pages/CreateProject';
import CalendarView from './pages/CalendarView';
import TasksView from './pages/TasksView';
import CreateProjectModal from '../../components/ProjectManagement/CreateProjectModal';

const { FiTrello, FiList, FiPlus, FiCalendar, FiCheckSquare, FiChevronRight } = FiIcons;

const ProjectManagement = () => {
  const location = useLocation();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const navigation = [
    { name: 'Project Kanban', href: '/project-management/board', icon: FiTrello, color: 'purple' },
    { name: 'Project List', href: '/project-management/list', icon: FiList, color: 'blue' },
    { name: 'Task Management', href: '/project-management/tasks', icon: FiCheckSquare, color: 'indigo' },
    { name: 'Create Project', href: '/project-management/create', icon: FiPlus, color: 'green' },
    { name: 'Calendar View', href: '/project-management/calendar', icon: FiCalendar, color: 'orange' },
  ];

  const isActive = (href) => location.pathname === href;

  const getCurrentPageName = () => {
    const currentNav = navigation.find(item => isActive(item.href));
    return currentNav?.name || 'Project Management';
  };

  const getColorClass = (color) => {
    const colors = {
      purple: 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200',
      blue: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-200',
      green: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200',
      orange: 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200',
    };
    return colors[color] || colors.blue;
  };

  const handleCreateSuccess = () => {
    // Optionally show a success message or refresh data
    console.log('Project created successfully!');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Project Management</h1>
            <p className="text-gray-600">Organize projects, manage tasks, and track progress</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              Create Project
            </button>
            <div className="text-right">
              <p className="text-sm text-gray-500">Current View</p>
              <p className="text-lg font-semibold text-gray-900">{getCurrentPageName()}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Navigation Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Views</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {navigation.map((item, index) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={item.href}
                className={`flex flex-col items-center p-4 rounded-lg border transition-all hover:scale-105 ${
                  isActive(item.href)
                    ? `${getColorClass(item.color)} shadow-md transform scale-105`
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={item.icon} className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium text-center">{item.name}</span>
                {isActive(item.href) && (
                  <SafeIcon icon={FiChevronRight} className="w-3 h-3 mt-1 opacity-70" />
                )}
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Routes>
          <Route path="/" element={<ProjectBoard />} />
          <Route path="/board" element={<ProjectBoard />} />
          <Route path="/list" element={<ProjectList />} />
          <Route path="/tasks" element={<TasksView />} />
          <Route path="/create" element={<CreateProject />} />
          <Route path="/calendar" element={<CalendarView />} />
        </Routes>
      </motion.div>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default ProjectManagement;